import React, { useState } from "react";
import { StyleSheet, Platform, View, Dimensions } from "react-native";
import { useDispatch, useSelector } from "react-redux";
import LHSectionHeader from "../../../components/LHSectionHeader";
import LHOptionCard from "../../../components/LHOptionCard";
import { useTheme } from "../../../theme";
import {
  toggleOption,
  selectSelectedOptions,
  selectImages,
  selectWalkState,
} from "../../../store/slices/leadershipWalkRedirectSlice";
import LeadershipWalkRedirectActivityImageUpload from "./LeadershipWalkRedirectActivityImageUpload";
import FontFamily from "../../../constants/fonts";

// Helper to detect iPad
const isIPad = Platform.OS === "ios" && Dimensions.get("window").width >= 768;

interface Option {
  id: string;
  label: string;
  details?: string; // Add details field for the flip functionality
}

interface LeadershipWalkRedirectActivityProps {
  title: string;
  options: Option[];
  selectedIds?: string[];
  onSelect?: (id: string) => void;
  error?: boolean;
  isDisabled?: boolean;
  testID?: string;
}

const LeadershipWalkRedirectActivity: React.FC<
  LeadershipWalkRedirectActivityProps
> = ({
  title,
  options,
  selectedIds = [],
  onSelect,
  error = false,
  isDisabled = false,
}) => {
  const dispatch = useDispatch();
  const { colors, resolvedTheme } = useTheme();
  const isDarkTheme = resolvedTheme === "dark";
  const selectedOptions = useSelector(selectSelectedOptions);
  const leadershipImages = useSelector(selectImages);
  const walkState = useSelector(selectWalkState);
  const [imageUploadModalVisible, setImageUploadModalVisible] =
    useState<boolean>(false);
  const [selectedActivity, setSelectedActivity] = useState<Option | null>(null);

  const handleToggleOption = (option: Option): void => {
    dispatch(
      toggleOption({
        id: option.id,
        label: option.label,
        details: option.details,
      })
    );
  };

  const handleCameraPress = (option: Option): void => {
    // Set the selected activity and open the modal
    setSelectedActivity(option);
    setImageUploadModalVisible(true);
  };

  const handleCloseModal = (): void => {
    setImageUploadModalVisible(false);
  };

  // Calculate justifyContent based on number of cards per row
  const getJustifyContent = (): "flex-start" | "center" => {
    const totalOptions = options.length;

    // On iOS iPad, always use flex-start (all cards start from left)
    if (isIPad) {
      return "flex-start";
    }

    if (Platform.OS === "web") {
      // On web, with flexWrap and gap, cards will naturally flow
      // Use flex-start for 1-2 cards, center only for 3+ cards
      const cardsPerRow = Math.floor((100 - 32) / (200 + 16)); // Approximate cards per row calculation
      const lastRowCards = totalOptions % cardsPerRow;

      // Use center only when we have 3 or more cards in a row
      return lastRowCards >= 3 || (cardsPerRow >= 3 && totalOptions >= 3)
        ? "center"
        : "flex-start";
    } else {
      // On mobile iOS (non-iPad), use flex-start for 1-2 cards, center only for 3+ cards
      return totalOptions >= 3 ? "center" : "flex-start";
    }
  };

  return (
    <View style={[styles.container, error && styles.errorContainer]}>
      {/* Card with integrated header and body */}
      <View style={styles.cardContainer}>
        {/* Header Section */}
        <LHSectionHeader
          title={title}
          containerStyle={{
            ...styles.sectionHeader,
            backgroundColor: colors.brand.primary,
          }}
          textStyle={styles.header1text}
        />

        {/* Body Section */}
        <View
          style={[
            styles.optionGrid,
            styles.optionCardWrapper,
            isDarkTheme && {
              backgroundColor: colors.background.surface,
              borderColor: colors.border.default,
            },
            { justifyContent: getJustifyContent() },
          ]}
        >
          {options.map((option) => (
            <LHOptionCard
              key={option.id}
              title={option.label}
              selected={selectedOptions.some(
                (item: Option) => item.id === option.id
              )}
              onPress={() => handleToggleOption(option)}
              disabled={walkState === "completed"}
              onCameraPress={() => handleCameraPress(option)}
              hasUploadedImage={leadershipImages.some(
                (img) => img.observationId === option.id
              )}
              detailsText={option.details}
            />
          ))}
        </View>
      </View>

      {/* Image Upload Modal */}
      {selectedActivity && (
        <LeadershipWalkRedirectActivityImageUpload
          visible={imageUploadModalVisible}
          onClose={handleCloseModal}
          activityId={selectedActivity.id}
          activityLabel={selectedActivity.label}
        />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    width: "100%",
    marginTop: 24,
  },
  errorContainer: {
    borderColor: "#dc3545",
    borderWidth: 1,
  },
  cardContainer: {
    borderRadius: 6,
    // allow header to expand beyond original bounds when wrapping
    overflow: Platform.OS === "ios" ? "visible" : "hidden",
  },
  sectionHeader: {
    backgroundColor: "#3582E6",
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    // use minHeight so header can expand to 2 lines when needed
    minHeight: 48,
  },
  header1text: {
    fontSize: 14,
    fontWeight: "700",
    lineHeight: 20,
    fontFamily: FontFamily.bold,
  },
  optionGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    // justifyContent is now set dynamically based on number of cards per row
    gap: Platform.OS === "web" ? 16 : 6,
    borderColor: "#e9ecef",
    borderWidth: 1,
    borderTopWidth: 0,
    borderRadius: 6,
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0,
    padding: 16,
  },
  optionCardWrapper: {
    backgroundColor: "white",
  },
});

export default LeadershipWalkRedirectActivity;
